/**
 * Merchant Redemption Page (Stories 7.2, 7.3, 7.4, 7.8)
 *
 * Focused redemption processing interface for merchant staff and admins.
 * Four-step flow for secure, trackable redemptions.
 *
 * Flow:
 * 1. Cashier enters 4-digit code (CashierCodeEntry) - Story 7.2
 * 2. Scan QR or enter coupon code (CouponScanEntry) - Story 7.3
 * 3. Process redemption with bill details (RedemptionForm) - Story 7.4
 * 4. Success screen with receipt (RedemptionSuccess) - Story 7.8
 *
 * Features:
 * - Cashier identification per transaction
 * - QR code scanning with camera
 * - Manual coupon code entry option
 * - Coupon validation before redemption
 * - Invoice ID tracking
 * - Success screen with printable receipt
 * - Responsive design with mobile support
 */

import React, { useState, useCallback } from 'react';
import { useCurrentMerchant } from '@/hooks-v2/context';
import { useRedemptionActions, useCashierActions } from '@/hooks-v2/coordinator';
import type {
	CreateRedemptionBodyType,
	CreateRedemptionResponseType,
	CheckCouponCodeResponseType,
} from '@zlinkx/shared-v2';
import { useTranslation } from '@/i18n';
import RedemptionForm from '@/components/merchant/RedemptionForm';
import CashierCodeEntry from '@/components/merchant/CashierCodeEntry';
import CouponScanEntry from '@/components/merchant/CouponScanEntry';
import RedemptionSuccess from '@/components/merchant/RedemptionSuccess';
import { ScanLine, Clock, User, RefreshCw, Ticket, ArrowLeft } from 'lucide-react';

type ValidatedCoupon = NonNullable<CheckCouponCodeResponseType['data']['coupon']>;

interface CashierInfo {
	cashierId: number;
	cashierName: string;
	code: string;
}

interface FormContext {
	invoiceId: string;
	note: string;
}

const Redemption: React.FC = () => {
	const { t } = useTranslation();

	const { selectedMerchant, selectedMerchantId, loading: isLoadingMerchants } = useCurrentMerchant();
	const selectedMerchantName = selectedMerchant?.merchantName ?? '';

	// Story 7.2: Cashier state - required before each redemption
	const [currentCashier, setCurrentCashier] = useState<CashierInfo | null>(null);

	// Story 7.3: Validated coupon state - set after QR scan or manual entry
	const [validatedCoupon, setValidatedCoupon] = useState<ValidatedCoupon | null>(null);

	// Story 7.8: Redemption result and form context for success screen
	const [redemptionResult, setRedemptionResult] = useState<CreateRedemptionResponseType | null>(null);
	const [formContext, setFormContext] = useState<FormContext | null>(null);

	// Cashier code validation - using coordinator pattern
	const cashierActions = useCashierActions();

	// Redemption processing hook - using coordinator pattern
	const redemptionActions = useRedemptionActions({
		onRedeemError: (error) => {
			// Error will be handled by RedemptionForm component
			console.error('Redemption error in coordinator:', error);
		}
	});

	// Processing state for backwards compatibility
	const [isProcessing, setIsProcessing] = useState(false);

	// Handle cashier code validation
	const handleValidateCashierCode = useCallback(
		async (code: string) => {
			const result = await cashierActions.validateCashierCode(selectedMerchantId ?? 0, code);
			return result;
		},
		[cashierActions, selectedMerchantId]
	);

	// Story 7.3: Handle coupon code validation (QR or manual)
	const handleCheckCouponCode = useCallback(
		async (params: { merchantId: number; data: { code: string } }) => {
			const result = await cashierActions.validateCouponCode(
				params.merchantId,
				params.data.code,
			);
			return result;
		},
		[cashierActions]
	);

	// Handle cashier validated
	const handleCashierValidated = useCallback(
		(info: { cashierId: number; cashierName: string; code: string }) => {
			setCurrentCashier({
				...info,
			});
		},
		[]
	);

	// Handle change cashier (manual reset)
	const handleChangeCashier = useCallback(() => {
		setCurrentCashier(null);
		setValidatedCoupon(null);
	}, []);

	// Story 7.3: Handle coupon validated from CouponScanEntry
	const handleCouponValidated = useCallback((coupon: ValidatedCoupon) => {
		setValidatedCoupon(coupon);
	}, []);

	// Handle going back to scan step (to scan a different coupon)
	const handleScanDifferentCoupon = useCallback(() => {
		setValidatedCoupon(null);
	}, []);

	// Handle redemption processing through RedemptionForm
	const handleRedemption = useCallback(
		async (data: CreateRedemptionBodyType): Promise<CreateRedemptionResponseType> => {
			setIsProcessing(true);
			try {
					const result = await redemptionActions.redeemCoupon(
						selectedMerchantId ?? 0,
						data
					);
				return result;
			} finally {
				setIsProcessing(false);
			}
		},
			[redemptionActions, selectedMerchantId],
		);

	// Story 7.8: Handle successful redemption - lift result to page state for success screen
	const handleRedeemSuccess = useCallback(
		(result: CreateRedemptionResponseType, context: FormContext) => {
			setRedemptionResult(result);
			setFormContext(context);
		},
		[]
	);

	// Story 7.8 AC5: New Redemption resets ALL state including cashier (security)
	const handleNewRedemption = useCallback(() => {
		setRedemptionResult(null);
		setFormContext(null);
		setCurrentCashier(null);
		setValidatedCoupon(null);
	}, []);

	// Loading state
	if (isLoadingMerchants) {
		return (
			<div className='flex items-center justify-center min-h-screen'>
				<div className='text-center'>
					<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4'></div>
					<p className='text-gray-600'>{t('merchant.redemption.loadingMerchant')}</p>
				</div>
			</div>
		);
	}

	if (selectedMerchantId === null) {
		return (
			<div className='flex items-center justify-center min-h-screen'>
				<div className='text-center'>
					<h2 className='text-2xl font-bold text-gray-900 mb-4'>
						{t('merchant.redemption.noMerchantTitle')}
					</h2>
					<p className='text-gray-600'>{t('merchant.redemption.noMerchantMessage')}</p>
				</div>
			</div>
		);
	}

	// Step 4: Success screen (check FIRST before other steps per Dev Notes)
	if (redemptionResult && validatedCoupon && formContext) {
		return (
			<div className='space-y-4 sm:space-y-6 p-4 sm:p-6'>
				<RedemptionSuccess
					redemptionResult={redemptionResult}
					validatedCoupon={validatedCoupon}
					formContext={formContext}
					onNewRedemption={handleNewRedemption}
				/>
			</div>
		);
	}

	return (
		<div className='space-y-4 sm:space-y-6 p-4 sm:p-6'>
			{/* Header */}
			<div className='bg-white rounded-xl shadow-sm border p-4 sm:p-6'>
				<div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
					<div className='flex items-center gap-3'>
						<div className='w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center'>
							<ScanLine className='w-6 h-6 text-blue-600' />
						</div>
						<div>
							<h1 className='text-xl sm:text-2xl font-bold text-gray-900'>
								{t('merchant.redemption.title')}
							</h1>
								<p className='text-gray-600 mt-1 text-sm sm:text-base'>
									{t('merchant.redemption.subtitle', { merchantName: selectedMerchantName })}
								</p>
						</div>
					</div>
					<div className='flex items-center gap-2'>
						{/* Current Cashier Badge (if validated) */}
						{currentCashier && (
							<div className='flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg'>
								<User className='w-4 h-4 text-green-600' />
								<span className='text-sm font-medium text-green-700'>
									{currentCashier.cashierName}
								</span>
								<button
									onClick={handleChangeCashier}
									className='ml-1 p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors'
									title={t('merchant.cashierCode.changeCashier')}
								>
									<RefreshCw className='w-3 h-3' />
								</button>
							</div>
						)}
						<a
							href='/merchant/redemption-history'
							className='inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors'
						>
							<Clock className='w-4 h-4' />
							<span>{t('merchant.redemption.quickActions.viewHistory')}</span>
						</a>
					</div>
				</div>
			</div>

			{/* Step 1: Cashier Code Entry (Story 7.2) */}
			{!currentCashier ? (
				<CashierCodeEntry
					onValidated={handleCashierValidated}
					onValidate={handleValidateCashierCode}
					isValidating={cashierActions.isValidating}
				/>
			) : !validatedCoupon ? (
				/* Step 2: Coupon Scan/Entry (Story 7.3) */
					<CouponScanEntry
						merchantId={selectedMerchantId}
					onCouponValidated={handleCouponValidated}
					onCancel={handleChangeCashier}
					onCheckCouponCode={handleCheckCouponCode}
					isCheckingCoupon={cashierActions.isValidatingCoupon}
				/>
			) : (
				/* Step 3: Redemption Form (Story 7.4) */
				<div className='space-y-4'>
					{/* Validated Coupon Summary Card */}
					<div className='bg-green-50 border border-green-200 rounded-xl p-4 sm:p-6'>
						<div className='flex items-start justify-between'>
							<div className='flex items-center gap-3'>
								<div className='w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center'>
									<Ticket className='w-6 h-6 text-green-600' />
								</div>
								<div>
									<h3 className='font-semibold text-green-800'>{validatedCoupon.title}</h3>
										<p className='text-sm text-green-600'>
											{validatedCoupon.couponType === 'percentage'
												? t('common.coupon.percentageOff', { value: validatedCoupon.discountValue ?? 0 })
												: validatedCoupon.couponType === 'fixed_amount'
													? t('common.coupon.amountOff', { value: (validatedCoupon.discountValue ?? 0) / 100 })
												: validatedCoupon.itemDescription ?? validatedCoupon.couponType}
									</p>
									{validatedCoupon.customerName && (
										<p className='text-xs text-green-600 mt-1'>
											{t('merchant.redemption.success.customerLabel')}
											{t('merchant.coupons.punctuation.colon')} {validatedCoupon.customerName}
										</p>
									)}
								</div>
							</div>
							<button
								onClick={handleScanDifferentCoupon}
								className='flex items-center gap-1 text-sm text-green-700 hover:text-green-900 transition-colors'
								title={t('merchant.couponEntry.success.scanAnother')}
							>
								<ArrowLeft className='w-4 h-4' />
								<span className='hidden sm:inline'>{t('merchant.couponEntry.success.scanAnother')}</span>
							</button>
						</div>
					</div>

					{/* Redemption Form */}
					<RedemptionForm
						onRedeem={handleRedemption}
						isLoading={redemptionActions.isLoading.redeemCoupon || isProcessing}
						cashierCode={currentCashier.code}
						validatedCoupon={validatedCoupon}
						onRedeemSuccess={handleRedeemSuccess}
					/>
				</div>
			)}

			{/* Quick Link to History */}
			<div className='flex justify-center'>
				<a
					href='/merchant/redemption-history'
					className='inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors'
				>
					<Clock className='w-4 h-4' />
					<span>{t('merchant.redemption.quickActions.viewHistory')}</span>
				</a>
			</div>
		</div>
	);
};

export default Redemption;
